<script lang="ts">
	import { PUBLIC_BACKEND } from '$app/env/public';
	import * as m from '$lib/paraglide/messages.js';
	import { resolve } from '$app/paths';
	import { localizeHref } from '@/paraglide/runtime';
	import CircleAlert from '@lucide/svelte/icons/circle-alert';
	import CircleCheck from '@lucide/svelte/icons/circle-check';
	import posthog from 'posthog-js';

	type NewsletterFeedback = 'idle' | 'success' | 'error';

	let email = $state('');
	let isLoading = $state(false);
	let acceptPrivacyPolicy = $state(false);
	let newsletterFeedback = $state<NewsletterFeedback>('idle');
	let newsletterMessage = $state('');

	function clearNewsletterFeedback() {
		newsletterFeedback = 'idle';
		newsletterMessage = '';
	}

	function showNewsletterError(message: string) {
		newsletterFeedback = 'error';
		newsletterMessage = message;
	}

	function showNewsletterSuccess(message: string) {
		newsletterFeedback = 'success';
		newsletterMessage = message;
	}

	async function handleSubscribe() {
		clearNewsletterFeedback();

		if (!email.trim()) {
			showNewsletterError(m.footer_err_email_invalid());
			return;
		}

		if (!acceptPrivacyPolicy) {
			showNewsletterError(m.footer_err_privacy_required());
			return;
		}

		isLoading = true;
		try {
			const response = await fetch(PUBLIC_BACKEND + '/newsletter', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ email: email.trim() })
			});

			if (!response.ok) {
				showNewsletterError(m.footer_err_subscribe_failed());
				return;
			}

			posthog.capture('newsletter_subscribed', {
				email: email.trim()
			});

			showNewsletterSuccess(m.footer_msg_success());
			email = '';
			acceptPrivacyPolicy = false;
		} catch {
			showNewsletterError(m.footer_err_connection());
		} finally {
			isLoading = false;
		}
	}

	function onNewsletterSubmit(event: SubmitEvent) {
		event.preventDefault();
		void handleSubscribe();
	}
</script>

<footer class="bg-zp-ink text-zp-paper/70">
	<div class="mx-auto max-w-[1240px] px-5 pt-16 pb-9 md:px-10 md:pt-[72px] md:pb-10">
		<div
			class="grid gap-10 border-b border-zp-paper/12 pb-11 md:grid-cols-2 md:gap-10 lg:grid-cols-[1.6fr_1fr_1fr_1fr]"
		>
			<div>
				<enhanced:img
					src="/static/img/landing/redesign-2025/logo-paper.png?w=320"
					alt="Zarpar"
					loading="lazy"
					sizes="160px"
					class="mb-5 h-[22px] w-auto"
				></enhanced:img>
				<p class="font-serif text-[1.18rem] leading-snug text-zp-paper/92 italic" lang="en">
					Helping all humans go sailing, one at a time.
				</p>
			</div>
			<div>
				<h4 class="mb-4 text-[0.72rem] font-semibold tracking-[0.18em] text-zp-paper/50 uppercase">
					{m.footer_mod_heading()}
				</h4>
				<a
					href={resolve(localizeHref('/alquiler-lista-septima'))}
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.footer_link_rental_list7()}</a
				>
				<a
					href={resolve(localizeHref('/zarpar-experiencias'))}
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.footer_link_experiences()}</a
				>
				<a
					href={resolve(localizeHref('/alquiler-lista-sexta'))}
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.footer_link_rental_list6()}</a
				>
				<a
					href={resolve(localizeHref('/registro-capitan'))}
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.footer_link_captain()}</a
				>
			</div>
			<div>
				<h4 class="mb-4 text-[0.72rem] font-semibold tracking-[0.18em] text-zp-paper/50 uppercase">
					{m.footer_brand_heading()}
				</h4>
				<a
					href="https://zarpar.eu/ayuda/blog/"
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.footer_link_blog()}</a
				>
				<a
					href="https://zarpar.eu/ayuda/"
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.patchy_round_cuckoo_stop()}</a
				>
				<a
					href={resolve(localizeHref('/regala-zarpar'))}
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.footer_link_gift_cards()}</a
				>
				<a
					href={resolve(localizeHref('/preguntas-frecuentes'))}
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.shy_ornate_capybara_accept()}</a
				>
			</div>
			<div>
				<h4 class="mb-4 text-[0.72rem] font-semibold tracking-[0.18em] text-zp-paper/50 uppercase">
					{m.free_calm_crocodile_link()}
				</h4>
				<p class="mb-3 text-[0.85rem] leading-snug text-zp-paper/55">
					{m.footer_hours()}
				</p>
				<a
					href="tel:+34679855762"
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.footer_phone()}</a
				>
				<a
					href="https://wa.me/34679855762"
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.footer_whatsapp()}</a
				>
				<a
					href="mailto:info@zarpar.eu"
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.footer_email()}</a
				>
				<a
					href={localizeHref('/contacto')}
					class="mb-2.5 block text-[0.92rem] text-zp-paper/78 transition-colors hover:text-white"
					>{m.footer_link_contact()}</a
				>
			</div>
		</div>

		<div class="py-10 md:py-11">
			<div
				class="grid items-start gap-8 md:grid-cols-2 md:gap-10 lg:grid-cols-[1.6fr_1fr_1fr_1fr] lg:gap-10"
			>
				<div>
					<h4
						id="footer-newsletter-heading"
						class="mb-4 text-[0.72rem] font-semibold tracking-[0.18em] text-zp-paper/50 uppercase"
					>
						{m.loose_bold_sloth_create()}
					</h4>
					<p class="text-[0.92rem] leading-snug text-zp-paper/78">
						{m.petty_white_deer_slurp()}
					</p>
				</div>

				<form
					class="w-full md:col-span-1 lg:col-span-3"
					onsubmit={onNewsletterSubmit}
					aria-labelledby="footer-newsletter-heading"
				>
					<div class="flex flex-col gap-3">
						<div>
							<label
								for="footer-newsletter-email"
								class="mb-2 block text-[0.72rem] font-semibold tracking-[0.18em] text-zp-paper/50 uppercase"
							>
								{m.zippy_quick_slug_arise()}
							</label>
							<input
								type="email"
								name="email"
								id="footer-newsletter-email"
								autocomplete="email"
								required
								bind:value={email}
								oninput={clearNewsletterFeedback}
								disabled={isLoading}
								aria-invalid={newsletterFeedback === 'error' ? true : undefined}
								aria-describedby={newsletterFeedback !== 'idle'
									? 'footer-newsletter-feedback'
									: undefined}
								class="w-full rounded-[10px] border border-zp-paper/15 bg-zp-paper/8 px-3.5 py-3 text-[0.92rem] text-zp-paper ring-1 ring-zp-paper/8 ring-inset outline-none focus:border-zp-paper/35 focus:ring-zp-paper/20 max-sm:text-base disabled:opacity-60"
							/>
						</div>

						<label
							class="flex cursor-pointer items-start gap-2.5 text-[0.83rem] leading-snug text-zp-paper/65"
						>
							<input
								type="checkbox"
								id="footer-accept-privacy"
								name="acceptPrivacyPolicy"
								bind:checked={acceptPrivacyPolicy}
								disabled={isLoading}
								required
								onchange={clearNewsletterFeedback}
								class="mt-0.5 size-5 shrink-0 accent-zp-green sm:size-4"
							/>
							<span>
								{m.footer_privacy_accept_before()}<a
									href={resolve(localizeHref('/politica-de-privacidad'))}
									target="_blank"
									rel="noopener noreferrer"
									class="text-zp-paper/90 underline underline-offset-2 transition-colors hover:text-white"
								>
									{m.each_basic_dachshund_value()}
								</a>{m.footer_privacy_accept_after()}
							</span>
						</label>

						<button
							type="submit"
							disabled={isLoading || !acceptPrivacyPolicy}
							class="inline-flex w-full items-center justify-center rounded-[10px] bg-zp-paper px-5 py-3 text-[0.92rem] font-semibold text-zp-ink transition-opacity hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:self-start"
						>
							{#if isLoading}
								{m.cool_noisy_hyena_embrace()}
							{:else}
								{m.mellow_gray_rat_express()}
							{/if}
						</button>

						{#if newsletterFeedback !== 'idle'}
							<div
								id="footer-newsletter-feedback"
								class="flex gap-3 rounded-[10px] border px-3.5 py-3 {newsletterFeedback ===
								'success'
									? 'border-zp-green/45 bg-zp-green/14'
									: 'border-red-300/40 bg-red-950/30'}"
								role={newsletterFeedback === 'success' ? 'status' : 'alert'}
								aria-live="polite"
							>
								{#if newsletterFeedback === 'success'}
									<CircleCheck class="mt-0.5 size-4 shrink-0 text-zp-green" aria-hidden="true" />
									<div class="text-[0.88rem] leading-snug text-zp-paper/92">
										<p class="font-semibold text-zp-paper">{m.footer_newsletter_success_title()}</p>
										<p class="mt-1">{newsletterMessage}</p>
									</div>
								{:else}
									<CircleAlert class="mt-0.5 size-4 shrink-0 text-red-200" aria-hidden="true" />
									<div class="text-[0.88rem] leading-snug text-red-100">
										<p class="font-semibold text-zp-paper">{m.footer_newsletter_error_title()}</p>
										<p class="mt-1">{newsletterMessage}</p>
									</div>
								{/if}
							</div>
						{/if}
					</div>
				</form>
			</div>
		</div>

		<div
			class="flex flex-wrap items-center justify-between gap-3 border-t border-zp-paper/12 pt-7 text-[0.8rem] text-zp-paper/50"
		>
			<span lang="gl"
				>© 2026 Plataforma Náutica Digital SL · Made a modiño e con cabeza en Galicia.</span
			>
			<span>
				<a href={resolve(localizeHref('/terminos-y-condiciones'))} class="hover:text-zp-paper/70"
					>{m.heavy_pretty_hornet_prosper()}</a
				>
				·
				<a href={resolve(localizeHref('/aviso-legal'))} class="hover:text-zp-paper/70"
					>{m.drab_zany_fish_tend()}</a
				>
				·
				<a href={resolve(localizeHref('/politica-de-privacidad'))} class="hover:text-zp-paper/70"
					>{m.footer_privacy_short()}</a
				>
				·
				<a href={resolve(localizeHref('/politica-de-cookies'))} class="hover:text-zp-paper/70"
					>{m.footer_cookies()}</a
				>
			</span>
		</div>
	</div>
</footer>

<!-- Big Zarpar banner — full-bleed wordmark (Tinybird-style SVG) -->
<section class="zp-footer-banner" aria-hidden="true">
	<div class="zp-footer-banner__frame">
		<svg
			class="zp-footer-banner__wordmark"
			viewBox="0 0 1000 200"
			preserveAspectRatio="xMidYMax meet"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
		>
			<text
				class="zp-footer-banner__text"
				x="0"
				y="188"
				textLength="1000"
				lengthAdjust="spacingAndGlyphs"
			>
				Zarpar
			</text>
		</svg>
	</div>
</section>

<style>
	.zp-footer-banner {
		overflow: hidden;
		background: #01141f;
		padding: 0;
	}

	.zp-footer-banner__frame {
		overflow: hidden;
		line-height: 0;
	}

	.zp-footer-banner__wordmark {
		display: block;
		width: 100%;
		height: auto;
		transform: scale(1.01) translateY(1%);
	}

	.zp-footer-banner__text {
		font-family: 'Dune Rise', Georgia, serif;
		font-size: 188px;
		font-weight: 400;
		fill: rgb(255 255 255 / 0.2);
	}
</style>
